"use client";

import * as React from "react";
import { createPortal } from "react-dom";

import { captureNotificationFocus, restoreNotificationFocus, type NotificationFocus } from "./focus-context";

import { cn } from "~/lib/utils";

export type ToastTone = "default" | "success" | "danger" | "warning";

export interface ToastItem {
  id: string;
  title?: string;
  description?: string;
  tone?: ToastTone;
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
  /** Capture before removing a control, so dismissing Undo returns to its context. */
  returnFocus?: NotificationFocus;
}

interface ToastCtx {
  toast: (t: Omit<ToastItem, "id">) => void;
  dismiss: (id: string) => void;
}
const Ctx = React.createContext<ToastCtx | null>(null);

export function useToast(): ToastCtx {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

/**
 * P14-10 · The same context, or null when there is no provider.
 *
 * For components that may legitimately render outside the app shell — the
 * product card is one, since `/kitchen-sink` renders it bare — and for which a
 * missing toaster should cost a notification, not the page. `useToast` keeps
 * throwing: a component that NEEDS to tell the user something and silently
 * cannot is the more dangerous default, and most callers are in the shell.
 */
export function useOptionalToast(): ToastCtx | null {
  return React.useContext(Ctx);
}

let counter = 0;

/** P0-11 · Global toast system. Mount once (admin shell / root). */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const contexts = React.useRef(new Map<string, NotificationFocus>());

  const dismiss = React.useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      const context = contexts.current.get(id);
      if (element?.contains(document.activeElement) && context) restoreNotificationFocus(context);
      contexts.current.delete(id);
      setToasts((list) => list.filter((t) => t.id !== id));
    },
    [],
  );
  const toast = React.useCallback(
    (t: Omit<ToastItem, "id">) => {
      const id = `toast-${++counter}`;
      const returnFocus = t.returnFocus ?? captureNotificationFocus();
      contexts.current.set(id, returnFocus);
      setToasts((list) => [...list, { ...t, id, returnFocus }]);
    },
    [],
  );

  return (
    <Ctx.Provider value={{ toast, dismiss }}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

function toneClass(tone: ToastTone | undefined): string {
  switch (tone) {
    case "success":
      return "bg-success text-success-fg";
    case "danger":
      return "bg-danger text-danger-fg";
    case "warning":
      return "bg-warning text-warning-fg";
    default:
      return "border border-border bg-surface text-fg";
  }
}

function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  const [mounted, setMounted] = React.useState(false);
  const [globalTarget, setGlobalTarget] = React.useState<HTMLDivElement | null>(null);
  const [panels, setPanels] = React.useState<HTMLElement[]>([]);
  React.useEffect(() => {
    setMounted(true);
    const update = () => {
      const next = Array.from(document.querySelectorAll<HTMLElement>("[data-dialog-id]"));
      // Toast insertion/movement also changes the DOM. Only modal inventory
      // changes require rendering, preventing an observer/portal feedback loop.
      setPanels((previous) => previous.length === next.length && previous.every((panel, i) => panel === next[i]) ? previous : next);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  if (!mounted) return null;

  return <>
    {createPortal(
      <div ref={setGlobalTarget} style={{ zIndex: panels.length ? 40 : 60 }}
        className="fixed bottom-4 right-4 flex max-h-[calc(100dvh-2rem)] w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 overflow-y-auto overscroll-contain" />,
      document.body,
    )}
    {toasts.map((toast) => <ToastNotification key={toast.id} toast={toast} onDismiss={onDismiss}
      target={panels.find((panel) => panel.dataset.dialogId === toast.returnFocus?.dialogId) ?? globalTarget} />)}
  </>;
}

function ToastNotification({ toast: t, onDismiss, target }: { toast: ToastItem; onDismiss: (id: string) => void; target: HTMLElement | null }) {
  // Keep this portal container stable. Moving the same DOM host preserves the
  // action's once-only claim and timer/busy/error state when its modal closes.
  const [host] = React.useState(() => document.createElement("div"));
  React.useEffect(() => {
    if (!target) return;
    host.className = target.dataset.dialogId ? "mt-3 min-w-0" : "min-w-0";
    target.appendChild(host);
    return () => { host.remove(); };
  }, [host, target]);

  const ref = React.useRef<HTMLDivElement>(null);
  const remaining = React.useRef(4000);
  const used = React.useRef(false);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState(false);
  const actionable = !!(t.actionLabel && t.onAction);

  React.useEffect(() => {
    // Actions have no time limit. Plain notices count down only while idle.
    if (actionable || hovered || focused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => onDismiss(t.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [actionable, hovered, focused, onDismiss, t.id]);

  const dismiss = () => {
    if (ref.current?.contains(document.activeElement) && t.returnFocus) restoreNotificationFocus(t.returnFocus);
    onDismiss(t.id);
  };
  const act = async () => {
    if (used.current) return;
    used.current = true;
    setBusy(true);
    try {
      await t.onAction?.();
      dismiss();
    } catch {
      // The outcome may be ambiguous: do not execute the same action twice.
      setActionError(true);
      setBusy(false);
    }
  };

  return createPortal(
    <div ref={ref} id={t.id} role="status" data-toast-dialog={t.returnFocus?.dialogId}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
      className={cn("grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-lg p-3 shadow-[var(--shadow-lg)]", toneClass(t.tone))}>
      <div className="col-span-2 min-w-0 break-words">
        {t.title && <div className="text-sm font-semibold">{t.title}</div>}
        {t.description && <div className="text-xs opacity-90">{t.description}</div>}
        {actionError && <div className="text-xs">The action could not be confirmed. Check the current state before trying again.</div>}
      </div>
      {actionable && <button type="button" aria-disabled={busy || actionError} aria-busy={busy} onClick={() => { void act(); }}
        className="max-w-full text-left text-xs font-semibold underline underline-offset-2">
        {t.actionLabel}
      </button>}
      <button type="button" onClick={dismiss} aria-label="Dismiss notification" className="col-start-2 justify-self-end opacity-70 transition-opacity hover:opacity-100">✕</button>
    </div>,
    host,
  );
}
